import { ArrowLeft, ChevronRight, MapPin, MoveDownRight, MoveUpRight } from 'lucide-react';
import { motion } from 'motion/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';

import { LinkButton } from '@/components/LinkButton';
import { destinations } from '@/lib/destinations';
import { Route, routes } from '@/lib/routes';

interface RouteDetailProps {
  route: Route;
}

export default function RouteDetail({ route }: RouteDetailProps) {
  const distanceFormatted = `${route.distanceKm.toString().replace('.', ',')} km`;
  const cleanTitle = route.title.replace(/^\d+\.\s*den:\s*/i, '');

  return (
    <>
      <Head>
        <title>{`${route.title} | DayZ Trip 2026`}</title>
        <meta
          name="description"
          content={`${route.title} - ${route.subtitle}`}
        />
      </Head>

      <main
        className="overflow-y-none mx-auto flex w-full max-w-4xl flex-1 flex-col gap-6"
        id="main_content"
      >
        {/* Dynamic Detail Card with Motion */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden bg-white shadow-sm"
        >
          {/* Hero Image Section */}
          <div className="relative flex min-h-64 w-full flex-col justify-end overflow-hidden p-6 text-left sm:min-h-96 sm:p-8">
            {route.image ? (
              <img
                src={route.image}
                alt={route.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-slate-900" />
            )}
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-linear-to-t from-slate-950/80 via-slate-950/30 to-transparent" />

            {/* Back Button */}
            <button
              onClick={() => window.history.back()}
              className="absolute top-4 left-4 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-slate-900/70 text-white transition-colors after:absolute after:-inset-8 after:content-[''] hover:bg-slate-900/80 active:scale-95"
              title="Zpět"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {/* Map Links Top Right */}
            <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
              {route.mapUrl && (
                <LinkButton
                  variant="mapycz"
                  type="icon"
                  href={route.mapUrl}
                />
              )}
            </div>

            {/* Title & Subtitle inside Hero */}
            <div className="relative z-10 flex flex-col text-white">
              <span className="text-3xl leading-12 font-bold tracking-tight text-white drop-shadow-sm">
                {route.day}. den
              </span>
              <h1 className="font-display mb-1 text-lg leading-snug font-bold text-white drop-shadow-md sm:text-xl">
                {cleanTitle}
              </h1>

              <div className="flex flex-col gap-0.5 text-xs font-medium text-slate-200 drop-shadow-sm sm:text-sm">
                <div className="flex items-center gap-1.5">
                  <span>
                    Délka: <strong className="font-semibold text-white">{distanceFormatted}</strong>
                  </span>
                  <span>&bull;</span>
                  <span>
                    Čas: <strong className="font-semibold text-white">{route.estimatedTime}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>Výškový profil:</span>
                  <span className="inline-flex items-center gap-0.5">
                    <MoveUpRight
                      strokeWidth="3"
                      className="h-3.5 w-3.5 text-emerald-500"
                    />
                    <strong className="font-semibold text-white">{route.elevationGainM} m</strong>
                  </span>
                  <span className="inline-flex items-center gap-0.5">
                    <MoveDownRight
                      strokeWidth="3"
                      className="h-3.5 w-3.5 text-emerald-500"
                    />
                    <strong className="font-semibold text-white">{route.elevationLossM} m</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Layout */}
          <div className="flex flex-col gap-6 p-4 sm:p-8">
            {/* Timeline of Stops */}
            {route.stops && route.stops.length > 0 && (
              <div className="rounded-xl border border-slate-200/80 bg-slate-100/50 p-4 text-xs text-slate-700 sm:text-sm">
                <div className="mb-3 flex items-center gap-2 font-bold text-emerald-900">
                  <MapPin className="h-4 w-4 shrink-0 text-emerald-600" />
                  Zastávky na trase ({route.stops.length})
                </div>
                <div className="relative ml-2 space-y-4 border-l-2 border-emerald-200 pl-4">
                  {route.stops.map((stop, idx) => {
                    const dest = stop.destinationId
                      ? destinations.find((d) => d.id === stop.destinationId)
                      : null;
                    return (
                      <div
                        key={idx}
                        className="relative"
                      >
                        <div
                          className={`absolute top-1 -left-[21px] h-2.5 w-2.5 rounded-full border-2 border-white ${
                            stop.isMainDestination
                              ? 'bg-emerald-600 ring-2 ring-emerald-300'
                              : 'bg-slate-400'
                          }`}
                        />
                        <div>
                          <div className="flex items-center justify-between gap-2">
                            {stop.destinationId ? (
                              <Link
                                href={`/destination/${stop.destinationId}`}
                                className="flex items-center gap-1 font-bold text-slate-900 hover:text-emerald-700 hover:underline"
                              >
                                <span>{stop.name}</span>
                                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                              </Link>
                            ) : (
                              <span className="font-bold text-slate-900">{stop.name}</span>
                            )}
                            {dest && (
                              <Link
                                href={`/destination/${dest.id}`}
                                className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline"
                              >
                                <img
                                  src={dest.imageUrl}
                                  alt={dest.name}
                                  className="h-6 w-6 rounded-md object-cover"
                                />
                              </Link>
                            )}
                          </div>
                          {stop.subtitle && (
                            <span className="block text-[11px] font-medium text-slate-500">
                              {stop.subtitle}
                            </span>
                          )}
                          {stop.description && (
                            <p className="mt-1 text-xs leading-normal text-slate-600">
                              {stop.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = routes.map((route) => ({
    params: { id: route.id },
  }));
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps<RouteDetailProps> = async ({ params }) => {
  const id = params?.id as string;
  const route = routes.find((r) => r.id === id);

  if (!route) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      route,
    },
  };
};
